<script setup lang="ts">
import type { LegalPortalURL } from '../../composables/portalUrls';
import type { CookieApp, CookieType, CookieTypeConfig } from '../../types/cookies';
import { computed } from 'vue';
import { useLegalPortalURL } from '../../composables/portalUrls';
import { useBottomDialog } from '../../composables/useBottomDialog';
import { useCookieAppConfig } from '../../composables/useCookieAppConfig';
import { useCookieConsent } from '../../composables/useCookieConsent';
import BottomDialogContainer from '../BottomDialog/Container.vue';
import BottomDialogMore from '../BottomDialog/More.vue';
import BottomDialogNotification from '../BottomDialog/Notification.vue';
import BottomDialogRoot from '../BottomDialog/Root.vue';
import Button from '../Button.vue';
import CookieSettingsItem from './SettingsItem.vue';

const props = withDefaults(defineProps<{
	app: CookieApp;
	notificationLabel?: string;
}>(), {
	notificationLabel: 'We use optional cookies to analyze traffic and make improvements to website. If you reject optional cookies, only necessary cookies will be used for the website to function. You may change your selection by choosing "Manage" button.',
});

const {
	isVisible: showMore,
	setVisible: setShowMore,
	show: showMoreDialog,
	hide: hideMoreDialog,
} = useBottomDialog<CookieApp>({
	key: 'cookie-settings',
	appId: props.app,
});

const { availableTypes, getTypeConfig } = useCookieAppConfig(props.app);
const { byType, dismissFor, isDismissed, setAll, ensureFunctionalEnabled } = useCookieConsent(props.app);

const cookieTypeConfigs = computed<(CookieTypeConfig & { type: CookieType })[]>(() =>
	availableTypes.value
		.map(type => getTypeConfig(type).value)
		.filter((c): c is CookieTypeConfig & { type: CookieType } => !!c && !!c.type),
);

const enabledByType = byType; // alias for template
const setEnabledByType = (type: CookieType, value: boolean) => {
	enabledByType[type].value = value;
};

const expireCookie = (name: string) => {
	if (!import.meta.client) {
		return;
	}

	const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
	const host = location.hostname;
	const hostParts = host.split('.');

	const domainVariants: string[] = [];
	for (let i = 0; i < hostParts.length; i += 1) {
		const domain = hostParts.slice(i).join('.');
		if (!domain) {
			continue;
		}
		domainVariants.push(domain, `.${domain}`);
	}

	const pathVariants = ['/', location.pathname || '/'];

	domainVariants.forEach((domain) => {
		pathVariants.forEach((path) => {
			document.cookie = `${name}=; expires=${expires}; path=${path}; domain=${domain}`;
		});
	});

	pathVariants.forEach((path) => {
		document.cookie = `${name}=; expires=${expires}; path=${path}`;
	});
};

const cookieRemovalPrefixes: Partial<Record<CookieType, string[]>> = {
	analytics: ['_ga', '_gid', '_gat', '_gcl_', '_ga_'],
};

const removeDisabledTypeCookies = () => {
	if (!import.meta.client) {
		return;
	}

	const existingCookieNames = document.cookie
		.split(';')
		.map(c => c.split('=')[0]?.trim())
		.filter(Boolean) as string[];

	cookieTypeConfigs.value.forEach((config) => {
		if (!config.cookies?.length) {
			return;
		}

		if (enabledByType[config.type].value) {
			return;
		}

		const namesToRemove: string[] = config.cookies
			.map(cookie => cookie.name?.trim())
			.filter(Boolean) as string[];

		const matchingPrefixes = cookieRemovalPrefixes[config.type];
		if (matchingPrefixes?.length) {
			const matched = existingCookieNames.filter(name =>
				matchingPrefixes.some(prefix => name.startsWith(prefix)),
			);
			namesToRemove.push(...matched);
		}

		namesToRemove.forEach(expireCookie);
	});
};

const acceptAll = () => {
	setAll(true);
	hideMoreDialog();
	dismissFor();
};

const rejectAll = () => {
	setAll(false);
	removeDisabledTypeCookies();
	hideMoreDialog();
	dismissFor();
};

const save = () => {
	ensureFunctionalEnabled();
	removeDisabledTypeCookies();
	hideMoreDialog();
	dismissFor();
};

const isVisible = computed(() => showMore.value || !isDismissed.value);
</script>

<template>
	<ClientOnly>
		<BottomDialogContainer v-if="isVisible">
			<BottomDialogRoot
				label="Cookie consent notice"
				:is-active="showMore"
			>
				<BottomDialogNotification
					:description="props.notificationLabel"
					icon="cookie"
					v-if="!showMore"
				>
					<template #actions>
						<Button
							label="Accept all"
							severity="dark"
							variant="solid"
							size="sm"
							aria-label="Accept all cookies"
							@click="acceptAll()"
						/>
						<Button
							label="Reject all"
							severity="dark"
							variant="soft"
							size="sm"
							aria-label="Reject optional cookies"
							@click="rejectAll()"
						/>
						<Button
							label="Manage"
							severity="dark"
							variant="soft"
							size="sm"
							aria-label="Open cookie settings"
							aria-haspopup="dialog"
							:aria-expanded="showMore"
							aria-controls="bottom-dialog-more"
							@click="showMoreDialog()"
						/>
					</template>
				</BottomDialogNotification>
				<BottomDialogMore
					title="Manage cookies"
					close-label="Close cookie settings"
					:is-active="showMore"
					v-else
					@update:is-active="setShowMore"
				>
					<div class="cookie-settings">
						<p
							class="cookie-settings__description"
							id="cookie-settings-description"
						>
							Cookies are small text files stored on your device when you visit a website or use an application.
							They help remember your preferences, such as language settings or region, and make navigation smoother and faster.
							Cookies do not harm your device and do not contain sensitive information like personal data, email addresses, or payment details.
							<a
								:href="useLegalPortalURL('cookie-notice') as LegalPortalURL"
								target="_blank"
								rel="noopener noreferrer"
							>
								Learn more about cookies
							</a>.
						</p>
						<div class="cookie-settings__list">
							<CookieSettingsItem
								:config="config"
								:enabled="enabledByType[config.type].value"
								v-for="config in cookieTypeConfigs"
								:key="config.type"
								@update:enabled="setEnabledByType(config.type, $event)"
							/>
						</div>
					</div>

					<template #footer>
						<Button
							label="Accept all"
							severity="dark"
							variant="solid"
							size="sm"
							aria-label="Accept all cookies"
							@click="acceptAll()"
						/>
						<Button
							label="Reject all"
							severity="dark"
							variant="soft"
							size="sm"
							aria-label="Reject optional cookies"
							@click="rejectAll()"
						/>
						<Button
							label="Save"
							severity="dark"
							variant="soft"
							size="sm"
							aria-label="Save cookie preferences"
							@click="save()"
						/>
					</template>
				</BottomDialogMore>
			</BottomDialogRoot>
		</BottomDialogContainer>
	</ClientOnly>
</template>

<style scoped lang="scss">
@layer hm-components
{
	.cookie-settings
	{
		@include flex(null, null, var(--spacing-lg));
		flex-direction:column;

		&__description
		{
			@include text-caption;

			a
			{
				text-decoration:underline;
				transition:var(--transition-fast);

				@include hover
				{
					color:var(--accent-primary);
				}
			}
		}
		&__list
		{
			@include flex(null, null, var(--spacing-2xs));
			flex-direction:column;
		}
	}
}
</style>
